import { AppConfigurationClient } from "@azure/app-configuration";
import { AZURE_CONFIG, FEATURES } from "./appConfig";

// Initialize the Azure App Configuration client
let configClient = null;
const configCache = {};

// Initialize client with connection string
if (AZURE_CONFIG.connectionString) {
  configClient = new AppConfigurationClient(AZURE_CONFIG.connectionString);
  if (FEATURES.LOG_CONFIG_LOADING) {
    console.log(
      `Azure App Configuration initialized for environment: ${AZURE_CONFIG.environment}`
    );
  }
} else if (FEATURES.LOG_CONFIG_LOADING) {
  console.warn(
    "Azure App Configuration connection string not found. Using fallback values."
  );
}

/**
 * Fetch a configuration value from Azure App Configuration
 * @param {string} key - The configuration key to fetch
 * @param {string} defaultValue - Optional default value if key is not found
 * @returns {Promise<string>} The configuration value
 */
export async function getConfigValue(key, defaultValue = null) {
  // Return cached value if available
  if (configCache[key]) {
    return configCache[key];
  }

  if (!configClient) {
    console.warn(
      "Azure App Configuration client not initialized. Using default value for:",
      key
    );
    return defaultValue;
  }

  try {
    const setting = await configClient.getConfigurationSetting({
      key,
      label: AZURE_CONFIG.environment,
    });
    const value = setting.value;

    // Cache the value
    if (FEATURES.ENABLE_CONFIG_CACHING) {
      configCache[key] = value;
    }

    if (FEATURES.LOG_CONFIG_LOADING) {
      console.log(
        `Loaded config key "${key}" from Azure (env: ${AZURE_CONFIG.environment})`
      );
    }

    return value;
  } catch (error) {
    if (FEATURES.LOG_CONFIG_LOADING) {
      console.warn(
        `Could not fetch config key "${key}" from Azure:`,
        error.message,
        `Using default: ${defaultValue}`
      );
    }
    return defaultValue;
  }
}

/**
 * Fetch multiple configuration values from Azure App Configuration
 * @param {Object} keyDefaults - Object with keys and their default values
 * @returns {Promise<Object>} Object with the same keys and fetched values
 */
export async function getConfigValues(keyDefaults) {
  const results = {};

  for (const [key, defaultValue] of Object.entries(keyDefaults)) {
    results[key] = await getConfigValue(key, defaultValue);
  }

  return results;
}

/**
 * Clear the configuration cache
 */
export function clearConfigCache() {
  Object.keys(configCache).forEach(key => delete configCache[key]);
}
